<template>
  <Teleport to="body">
    <Transition name="order-modal">
      <div
        v-if="isOpen"
        class="order-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-modal-title"
        @click.self="close"
      >
        <div class="order-modal__backdrop" aria-hidden="true" />
        <div class="order-modal__box" @click.self="close">
          <button type="button" class="order-modal__close" aria-label="Закрыть" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          <h2 id="order-modal-title" class="order-modal__title">
            {{ catalogItem ? 'Запрос на покупку' : 'Заказать картину' }}
          </h2>

          <!-- Режим витрины: карточка картины + форма запроса -->
          <template v-if="catalogItem">
            <div class="order-modal__catalog-card">
              <div class="order-modal__catalog-img-wrap">
                <img :src="catalogItem.imageUrl" :alt="catalogItem.title" class="order-modal__catalog-img" />
              </div>
              <div class="order-modal__catalog-info">
                <h3 class="order-modal__catalog-title">{{ catalogItem.title }}</h3>
                <p v-if="catalogItem.description" class="order-modal__catalog-desc">{{ catalogItem.description }}</p>
                <p v-if="catalogPrice" class="order-modal__catalog-price">{{ catalogPrice }}</p>
              </div>
            </div>
            <form class="order-modal__catalog-form" @submit.prevent="submitCatalogRequest">
              <FormInput
                v-model="catalogForm.name"
                name="catalog-name"
                label="Ваше имя"
                placeholder="Как к вам обращаться?"
                required
              />
              <FormInput
                v-model="catalogForm.contacts"
                name="catalog-contacts"
                label="Контакты"
                placeholder="Телефон, почта или мессенджер"
                required
              />
              <button type="submit" class="order-modal__btn order-modal__btn_submit order-modal__btn_full" :disabled="catalogSending">
                {{ catalogSending ? 'Отправка…' : 'Отправить запрос' }}
              </button>
            </form>
          </template>

          <!-- Многошаговый заказ (без выбора из витрины) -->
          <template v-else>
          <!-- Прогресс шагов -->
          <div class="order-modal__steps-bar">
            <button
              v-for="(step, i) in orderSteps"
              :key="step.id"
              type="button"
              class="order-modal__step-tab"
              :class="{ 'order-modal__step-tab_active': currentStepIndex === i, 'order-modal__step-tab_done': i < currentStepIndex }"
              :aria-current="currentStepIndex === i ? 'step' : null"
              @click="currentStepIndex = i"
            >
              <span class="order-modal__step-num">{{ i + 1 }}</span>
              <span class="order-modal__step-title">{{ step.title }}</span>
            </button>
          </div>

          <form class="order-modal__form" @submit.prevent="submit">
            <!-- Шаг 1: Размер -->
            <div v-show="currentStepIndex === 0" class="order-modal__panel">
              <p class="order-modal__hint">Выберите размер рамки</p>
              <div class="order-modal__tabs order-modal__tabs_size">
                <button
                  v-for="opt in sizeOptions.filter((o) => o.label)"
                  :key="opt.value"
                  type="button"
                  class="order-modal__tab"
                  :class="{ 'order-modal__tab_active': form.size === opt.value }"
                  @click="form.size = opt.value"
                >
                  {{ opt.label }}
                  <span v-if="opt.price" class="order-modal__tab-price">{{ formatPrice(opt.price) }}</span>
                </button>
              </div>
            </div>

            <!-- Шаг 2: Ориентация -->
            <div v-show="currentStepIndex === 1" class="order-modal__panel">
              <p class="order-modal__hint">Ориентация картины</p>
              <div class="order-modal__tabs order-modal__tabs_compact">
                <button
                  v-for="opt in orderSteps[1].options"
                  :key="opt.value"
                  type="button"
                  class="order-modal__tab"
                  :class="{ 'order-modal__tab_active': form.orientation === opt.value }"
                  @click="form.orientation = opt.value"
                >
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Шаг 3: Надпись -->
            <div v-show="currentStepIndex === 2" class="order-modal__panel">
              <FormInput
                v-model="form.inscription"
                :placeholder="orderSteps[2].placeholder"
                :name="orderSteps[2].name"
                label="Текст надписи (по желанию)"
              />
            </div>

            <!-- Шаг 4: Комментарий -->
            <div v-show="currentStepIndex === 3" class="order-modal__panel">
              <FormInput
                v-model="form.comment"
                :placeholder="orderSteps[3].placeholder"
                :name="orderSteps[3].name"
                label="Ваш комментарий"
                multiline
              />
            </div>

            <!-- Шаг 5: Контакты -->
            <div v-show="currentStepIndex === 4" class="order-modal__panel">
              <div class="order-modal__contacts">
                <FormInput
                  v-for="field in orderSteps[4].fields"
                  :key="field.id"
                  v-model="form.contacts[field.id]"
                  :name="field.name"
                  :type="field.type"
                  :label="field.label"
                  :placeholder="field.placeholder"
                  :required="field.required"
                />
              </div>
            </div>

            <!-- Шаг 6: Превью и оформление -->
            <div v-show="currentStepIndex === 5" class="order-modal__panel order-modal__panel_preview">
              <div class="order-modal__preview">
                <h3 class="order-modal__preview-title">Ваш заказ</h3>
                <dl class="order-modal__preview-list">
                  <dt>Размер</dt>
                  <dd>{{ selectedSizeLabel }} — {{ formattedPriceByKey(form.size) }}</dd>
                  <dt>Ориентация</dt>
                  <dd>{{ form.orientation === 'vertical' ? 'Вертикальная' : 'Горизонтальная' }}</dd>
                  <template v-if="form.inscription">
                    <dt>Надпись</dt>
                    <dd>{{ form.inscription }}</dd>
                  </template>
                  <template v-if="form.comment">
                    <dt>Комментарий</dt>
                    <dd>{{ form.comment }}</dd>
                  </template>
                  <template v-if="form.contacts.phone || form.contacts.email || form.contacts.social">
                    <dt>Контакты</dt>
                    <dd>
                      <span v-if="form.contacts.phone">{{ form.contacts.phone }}</span>
                      <span v-if="form.contacts.phone && form.contacts.email"> · </span>
                      <span v-if="form.contacts.email">{{ form.contacts.email }}</span>
                      <span v-if="(form.contacts.phone || form.contacts.email) && form.contacts.social"> · </span>
                      <span v-if="form.contacts.social">{{ form.contacts.social }}</span>
                    </dd>
                  </template>
                </dl>
              </div>
            </div>

            <div class="order-modal__actions">
              <button v-if="currentStepIndex > 0" type="button" class="order-modal__btn order-modal__btn_secondary" @click="currentStepIndex--">
                Назад
              </button>
              <template v-else>
                <span></span>
              </template>
              <template v-if="currentStepIndex < orderSteps.length - 1">
                <button type="button" class="order-modal__btn order-modal__btn_primary" @click="currentStepIndex++">
                  Далее
                </button>
              </template>
              <template v-else>
                <button type="submit" class="order-modal__btn order-modal__btn_submit" :disabled="orderSending">
                  {{ orderSending ? 'Отправка…' : ORDER_MODAL_SUBMIT_LABEL }}
                </button>
              </template>
            </div>
          </form>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { orderSteps, ORDER_MODAL_SUBMIT_LABEL } from '@/data/orderFormConfig'
import { formatPrice } from '@/data/prices'
import { getApiUrl } from '@/config/api'
import FormInput from '@/components/FormInput.vue'

const defaultForm = (defaultSize) => ({
  size: defaultSize || null,
  orientation: 'horizontal',
  inscription: '',
  comment: '',
  contacts: {
    phone: '',
    email: '',
    social: ''
  }
})

export default {
  name: 'OrderModal',
  components: { FormInput },
  setup() {
    const store = useStore()
    const isOpen = computed(() => store.state.orderModalOpen)
    const catalogItem = computed(() => store.state.orderModalCatalogItem)
    const currentStepIndex = ref(0)
    const form = reactive(defaultForm())
    const catalogForm = reactive({ name: '', contacts: '' })
    const catalogSending = ref(false)
    const orderSending = ref(false)

    const catalogPrice = computed(() => {
      if (!catalogItem.value?.sizeKey) return null
      return store.getters['price/formattedPriceByKey'](catalogItem.value.sizeKey)
    })

    const sizeOptions = computed(() => {
      const items = store.getters['price/priceRows'] || []
      return items.map((r) => ({
        value: r.sizeKey,
        label: r.sizeLabel,
        price: r.price
      }))
    })

    const selectedSizeLabel = computed(() => {
      const opt = sizeOptions.value.find((o) => o.value === form.size)
      return opt ? opt.label : form.size || '—'
    })

    function close() {
      store.commit('setOrderModalOpen', false)
    }

    const formattedPriceByKey = (key) => store.getters['price/formattedPriceByKey'](key)

    async function submit() {
      const { phone, email, social } = form.contacts
      if (!phone?.trim() && !email?.trim() && !social?.trim()) {
        store.commit('setToast', 'Укажите хотя бы один контакт')
        return
      }
      orderSending.value = true
      try {
        const res = await fetch(getApiUrl('/api/new-order'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            size: form.size,
            orientation: form.orientation,
            inscription: form.inscription?.trim() || '',
            comment: form.comment?.trim() || '',
            contacts: {
              phone: (phone || '').trim(),
              email: (email || '').trim(),
              social: (social || '').trim()
            }
          })
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
        close()
        form.inscription = ''
        form.comment = ''
        form.contacts = { phone: '', email: '', social: '' }
        store.commit('setToast', 'Заказ отправлен! Мы свяжемся с вами.')
      } catch (e) {
        console.error('New order submit error', e)
        store.commit('setToast', e.message || 'Ошибка отправки. Попробуйте позже.')
      } finally {
        orderSending.value = false
      }
    }

    async function submitCatalogRequest() {
      if (!catalogForm.name.trim() || !catalogForm.contacts.trim()) return
      catalogSending.value = true
      try {
        const item = catalogItem.value
        const payload = {
          name: catalogForm.name.trim(),
          contacts: catalogForm.contacts.trim(),
          item: item ? { id: item.id, title: item.title, description: item.description || '', sizeKey: item.sizeKey, imageUrl: item.imageUrl } : null
        }
        const res = await fetch(getApiUrl('/api/buy'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
        close()
        catalogForm.name = ''
        catalogForm.contacts = ''
        store.commit('setToast', 'Запрос отправлен! Мы свяжемся с вами.')
      } catch (e) {
        console.error('Buy request error', e)
        store.commit('setToast', e.message || 'Ошибка отправки. Попробуйте позже.')
      } finally {
        catalogSending.value = false
      }
    }

    function onKeydown(e) {
      if (e.key === 'Escape') close()
    }
    watch(isOpen, (open) => {
      if (open) {
        store.dispatch('price/fetchPrices')
        store.dispatch('catalog/fetchItems')
        currentStepIndex.value = 0
        catalogForm.name = ''
        catalogForm.contacts = ''
        const preselected = store.state.orderModalPreselectedSizeKey
        const size = preselected && sizeOptions.value.some((o) => o.value === preselected)
          ? preselected
          : (sizeOptions.value[0]?.value || null)
        Object.assign(form, defaultForm(size))
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.body.style.paddingRight = scrollbarWidth ? `${scrollbarWidth}px` : ''
        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeydown)
      } else {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
        window.removeEventListener('keydown', onKeydown)
      }
    })

    return {
      orderSteps,
      ORDER_MODAL_SUBMIT_LABEL,
      formatPrice,
      formattedPriceByKey,
      sizeOptions,
      selectedSizeLabel,
      isOpen,
      catalogItem,
      catalogPrice,
      catalogForm,
      catalogSending,
      orderSending,
      currentStepIndex,
      form,
      close,
      submit,
      submitCatalogRequest
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";

$modal-dur: 0.35s;
$modal-ease: cubic-bezier(0.22, 1, 0.36, 1);

.order-modal {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Arimo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.order-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
}

.order-modal__box {
  position: relative;
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: $color-primary-inverse;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
  padding: 32px 28px 28px;
  box-sizing: border-box;

  @media (max-width: $breakpoint-mobile) {
    padding: 24px 16px 20px;
    margin: 0 12px;
    max-height: 85vh;
  }
}

.order-modal__close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 50%;
  color: $color-text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: $color-text;
  }
  svg { width: 20px; height: 20px; }
}

.order-modal__form {
  @media (max-width: $breakpoint-mobile) {
    min-width: 0;
    max-width: 100%;
  }
}

.order-modal__title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 28px;
  padding-right: 48px;
  color: $color-text;
}

/* Режим витрины: карточка картины */
.order-modal__catalog-card {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 20px;
  margin-bottom: 28px;
  background: $color-bg;
  border: 1px solid $color-border;
  border-radius: 12px;
  border-left: 4px solid $color-accent-teal;

  @media (max-width: $breakpoint-mobile) {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }
}

.order-modal__catalog-img-wrap {
  flex-shrink: 0;
  width: 140px;
  aspect-ratio: 4 / 3;
  border-radius: 8px;
  overflow: hidden;
  background: $color-border;

  @media (max-width: $breakpoint-mobile) {
    width: 100%;
    max-width: 240px;
    margin: 0 auto;
  }
}

.order-modal__catalog-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.order-modal__catalog-info {
  min-width: 0;
  flex: 1;
}

.order-modal__catalog-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
  color: $color-text;
}

.order-modal__catalog-desc {
  font-size: 14px;
  line-height: 1.45;
  color: $color-text-muted;
  margin: 0 0 10px;
}

.order-modal__catalog-price {
  font-size: 17px;
  font-weight: 700;
  color: $color-accent-teal;
  margin: 0;
}

.order-modal__catalog-form {
  display: flex;
  flex-direction: column;
  gap: 20px;

  .order-modal__btn_full {
    width: 100%;
    margin-top: 8px;
    padding: 16px 24px;
  }
}

.order-modal__steps-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid $color-border;
}

.order-modal__step-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  color: $color-text-muted;
  background: rgba(0, 0, 0, 0.04);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: color $modal-dur $modal-ease, background $modal-dur $modal-ease;

  &:hover {
    color: $color-text;
    background: rgba(0, 0, 0, 0.08);
  }

  &_active {
    color: $color-primary-inverse;
    background: $color-accent-teal;
  }

  &_done {
    color: $color-accent-teal;
    background: rgba(13, 148, 136, 0.12);
  }
}

.order-modal__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 11px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);

  .order-modal__step-tab_active &,
  .order-modal__step-tab_done & {
    background: rgba(255, 255, 255, 0.9);
    color: $color-accent-teal;
  }
  .order-modal__step-tab_done & {
    background: $color-accent-teal;
    color: $color-primary-inverse;
  }
}

.order-modal__step-title {
  @media (max-width: $breakpoint-mobile) {
    display: none;
  }
}

.order-modal__panel {
  min-height: 140px;
  margin-bottom: 24px;

  @media (max-width: $breakpoint-mobile) {
    min-width: 0;
    :deep(.form-input) {
      max-width: 100%;
    }
    :deep(.form-input__field) {
      box-sizing: border-box;
      max-width: 100%;
    }
  }

  &_preview {
    min-height: 0;
  }
}

.order-modal__preview {
  padding: 20px;
  background: $color-bg;
  border: 1px solid $color-border;
  border-radius: 12px;
  border-left: 4px solid $color-accent-teal;
}

.order-modal__preview-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $color-accent-teal;
  margin: 0 0 16px;
}

.order-modal__preview-list {
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 20px;
  align-items: baseline;
  font-size: 15px;
  line-height: 1.45;

  dt {
    color: $color-text-muted;
    font-weight: 500;
  }
  dd {
    margin: 0;
    color: $color-text;
  }
}

.order-modal__hint {
  font-size: 14px;
  color: $color-text-muted;
  margin: 0 0 16px;
}

.order-modal__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  &_compact .order-modal__tab {
    flex: 1;
    min-width: 120px;
  }

  @media (max-width: $breakpoint-mobile) {
    &_size {
      flex-direction: column;
      gap: 8px;

      .order-modal__tab {
        width: 100%;
        flex: none;
        flex-direction: row;
        justify-content: space-between;
        text-align: left;
      }
    }
  }
}

.order-modal__tab {
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 600;
  color: $color-text;
  background: $color-bg;
  border: 2px solid $color-border;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color $modal-dur $modal-ease, background $modal-dur $modal-ease, color $modal-dur $modal-ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  &:hover {
    border-color: $color-accent-teal;
    background: rgba(13, 148, 136, 0.06);
  }

  &_active {
    border-color: $color-accent-teal;
    background: rgba(13, 148, 136, 0.12);
    color: $color-accent-teal;
  }
}

.order-modal__tab-price {
  font-size: 13px;
  font-weight: 700;
  color: $color-text-muted;
  .order-modal__tab_active & {
    color: $color-accent-teal;
  }
}

.order-modal__contacts {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
  max-width: 100%;
}

.order-modal__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  padding-top: 24px;
  border-top: 1px solid $color-border;
}

.order-modal__btn {
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &_secondary {
    background: rgba(0, 0, 0, 0.06);
    color: $color-text;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }

  &_primary {
    background: $color-accent-teal;
    color: $color-primary-inverse;

    &:hover {
      background: darken($color-accent-teal, 6%);
    }
  }

  &_submit {
    background: $color-primary;
    color: $color-primary-inverse;

    &:hover {
      background: lighten($color-primary, 10%);
    }
  }

  &_full {
    width: 100%;
  }
}

.order-modal-enter-active,
.order-modal-leave-active {
  transition: opacity $modal-dur $modal-ease;
  .order-modal__box {
    transition: transform $modal-dur $modal-ease;
  }
}
.order-modal-enter-from,
.order-modal-leave-to {
  opacity: 0;
  .order-modal__box {
    transform: scale(0.96) translateY(12px);
  }
}
</style>
